import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useFieldArray, useForm } from "react-hook-form";
import Lottie from "lottie-react";
import animationData from "../Scanning_Photos_file.json";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ROUTES } from "@/app/routes";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { format } from "date-fns";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import { RxCross2 } from "react-icons/rx";
import { useVaccinationOCRDataSubmissionAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationOCRDataSubmissionAPI";
import { usePetWiseVaccinationListAPI } from "@/app/hooks/api_hooks/Vaccination/usePetWiseVaccinationListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useVaccinationOCRUpdationAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationOCRUpdationAPI";
import { useVaccinationSingleRecordAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationSingleRecordAPI";
import { useVaccinationHistoryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationHistoryListAPI";
import { useNavigate } from "react-router";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  loading: boolean;
  setLoading?: (value: boolean) => void;
  setVaccinationData?: (s: any) => void;
  vaccinationData?: any;
  fileData: any;
  setFileData: (a: any) => void;
  mode: any;
  setMode: (a: any) => void;
  vaccination_id: string;
  vaccination_file: string;
  vaccinationErrorMessage?: string;
};

const OCRmodalComponent: React.FC<Props> = ({
  open,
  setOpen,
  loading,
  setVaccinationData,
  vaccinationData,
  fileData,
  setFileData,
  mode,
  setMode,
  vaccination_id,
  vaccination_file,
  vaccinationErrorMessage,
}) => {
  const cancelButtonRef = useRef(null);
  const { getCurrentDate } = useUtils();
  const navigate = useNavigate();
  const [toggle, setToggle] = useState<boolean>(false);
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { petDetails, customerId, petId } = routeState.state as ICustomerProfileState;
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const [initialState, setInitialState] = useState<boolean>(false);
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const { execute: submitOCRData } = useVaccinationOCRDataSubmissionAPI();
  const { execute: updateVaccinationOCRAPI } = useVaccinationOCRUpdationAPI();
  const { execute: singleDataFetch, singleRecord } = useVaccinationSingleRecordAPI();
  const { execute: petWiseVaccination, staticData } = usePetWiseVaccinationListAPI();
  const { execute: fetchVaccinationHistoryList } = useVaccinationHistoryListAPI();
  const isSingleItemFetching = useLoader("isvaccinationSingleDataLoading");
  const { setVisible } = useModalActions("VACCINATION_OVERDUE_MODAL");

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  const { control } = formHook;
  const {
    fields: vaccination,
    replace: updatedVaccination,
    append: addVaccination,
    remove: removeVaccine,
  } = useFieldArray({
    name: "vaccination_ocr",
    control,
  });

  function convertDateFormat(dateStr) {
    if (dateStr) {
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  const onSubmit = (data: any) => {
    setIsSubmitPending(true);
    const constructedBody = data?.vaccination_ocr?.map((item) => ({
      unique_dose_key: item?.unique_dose_key,
      given_date: item?.given_date,
    }));
    const payload = {
      source: "file-upload",
      user_id: customerId,
      pet_id: petId,
      clinic_id: mappedClinicId,
      vaccination_details: constructedBody,
      file_url: vaccinationData?.file_url,
    };
    const editPayload = {
      vaccination_id: vaccination_id,
      clinic_id: mappedClinicId,
      unique_dose_key: data?.vaccination_ocr[0]?.unique_dose_key,
      given_date: data?.vaccination_ocr[0]?.given_date,
    };
    if (mode === "EDIT") {
      updateVaccinationOCRAPI(editPayload).then(({ status }) => {
        if (status) {
          setOpen(false);
          setIsSubmitPending(false);
          setMode("UPLOAD");
          formHook.reset();
          fetchVaccinationHistoryList(petId, customerId);
        } else {
          setIsSubmitPending(false);
        }
      });
    } else {
      submitOCRData(payload, customerId, petId).then((res) => {
        if (res?.status) {
          setOpen(false);
          setIsSubmitPending(false);
          formHook.reset();
          setVisible();
          fetchVaccinationHistoryList(petId, customerId);
          navigate(
            `/app/customer/profile/customer-details/vaccination/${customerId}/${petId}?document_source=vaccination_history`,
          );
        } else {
          setIsSubmitPending(false);
        }
      });
    }
  };

  useEffect(() => {
    if (vaccinationData?.parsed_data?.length > 0 && !initialState && mode === "UPLOAD") {
      const updated_date = vaccinationData?.parsed_data?.map((item) => ({
        ...item,
        given_date: convertDateFormat(item?.given_date),
      }));
      updatedVaccination(updated_date);
      setInitialState(true);
    }
  }, [vaccinationData, initialState, mode]);

  useEffect(() => {
    if (singleRecord?.given_date && !initialState && mode === "EDIT") {
      const single_data = [
        {
          vaccine_name: singleRecord?.dose_name,
          dose_number: 1,
          given_date: singleRecord?.given_date,
          unique_dose_key: singleRecord?.unique_dose_key,
        },
      ];
      updatedVaccination(single_data);
      setInitialState(true);
    }
  }, [singleRecord, initialState, mode]);

  useEffect(() => {
    petWiseVaccination(petDetails?.pet_species_type_id);
  }, []);

  useEffect(() => {
    if (vaccination_id && mode === "EDIT") {
      const constructedBody = {
        vaccination_id: vaccination_id,
      };
      singleDataFetch(constructedBody);
    }
  }, [vaccination_id]);

  const staticDataIteams = useSelectMenuReducer(staticData, "dose_name", "unique_dose_key");

  const handelViewOcrFile = (image: string) => {
    window.open(image, "_blank");
  };

  const fileDataList: any[] = (() => {
    if (mode === "EDIT") {
      return Array.isArray(vaccination_file) ? vaccination_file : [];
    } else {
      return Array.isArray(vaccinationData?.file_url) ? vaccinationData.file_url : [];
    }
  })();

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            // setOpen(false);
            // formHook.reset();
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={formHook.handleSubmit(onSubmit)}
                className={`inline-block align-bottom bg-white rounded-2xl text-left  overflow-visible  shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full ${loading ? "sm:max-w-xl" : mode === "EDIT" ? "sm:max-w-3xl" : "sm:max-w-4xl"} `}
              >
                {loading ? (
                  <div className="flex items-center flex-col p-10">
                    <div className="w-64 h-64">
                      <Lottie animationData={animationData} loop={true} />
                    </div>
                    <p className="font-medium sm:text-xs 2xl:text-[10px] 2xl:text-sm">
                      We are processing the uploaded document{" "}
                    </p>
                  </div>
                ) : mode === "EDIT" && isSingleItemFetching ? (
                  <TableSkeleton />
                ) : (
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 rounded-3xl">
                    <div className="flex justify-between items-center mt-2  pb-2">
                      <h2 className="text-[10px] sm:text-xs 2xl:text-base font-semibold">
                        Uploading Document for <span className="  font-semibold">"{petDetails?.name}"</span>
                      </h2>

                      <span
                        onClick={() => {
                          updatedVaccination([]); // Clear the field array
                          setVaccinationData([]);
                          formHook.reset({ vaccination_ocr: [] }); // Ensure reset has matching structure
                          setInitialState(false); // Reset initial state
                          setFileData(null); // Optional: Clear fileData too
                          setOpen(false); // Close modal
                        }}
                      >
                        <RxCross2 className="h-5 w-5 fill-black cursor-pointer font-bold" />
                      </span>
                    </div>

                    {fileDataList?.length > 0 &&
                      fileDataList?.map((i) => {
                        const fileName = i?.split("/")?.pop();

                        return (
                          <div>
                            <div className="mt-2 ">
                              <div className=" rounded-xl p-4 border ">
                                <div className="flex justify-between text-[#475467] text-[10px] sm:text-xs 2xl:text-sm   ">
                                  <div className="flex flex-col">
                                    <p className="font-medium flex items-center gap-2 text-sm">
                                      <img
                                        src="https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/dscshIcon.svg"
                                        alt="copy"
                                      />{" "}
                                      <span className="-mt-1 font-medium">{fileName || "No File Available"}</span>
                                    </p>
                                  </div>

                                  <p
                                    className="text-[10px] sm:text-xs 2xl:text-sm text-violet-500 px-1 rounded flex items-center gap-1 font-semibold cursor-pointer"
                                    onClick={() => handelViewOcrFile(i)}
                                  >
                                    View <span>{/* <ArrowRightIcon className="w-4 h-4" /> */}</span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    <div className="max-w-4xl mx-auto pt-4 space-y-6">
                      {vaccination?.length > 0 ? (
                        <>
                          <div className="">
                            <div className="  border  overflow-visible">
                              <table className="w-full text-left">
                                <thead className="bg-[#F1F1F3] -z-20">
                                  <tr className=" text-[10px] sm:text-xs 2xl:text-sm text-muted-foreground">
                                    <th className="p-3 text-[10px] sm:text-xs 2xl:text-sm font-medium">SN</th>
                                    <th className="p-3 text-[10px] sm:text-xs 2xl:text-sm font-medium">Given Date</th>
                                    {mode === "UPLOAD" && (
                                      <th className="p-3 text-[10px] sm:text-xs 2xl:text-sm font-medium">
                                        Vaccine Name (via OCR)
                                      </th>
                                    )}
                                    <th className="p-3 text-[10px] sm:text-xs 2xl:text-sm font-medium">
                                      Vetic Vaccine Name
                                    </th>
                                    <th className="p-3 text-[10px] sm:text-xs 2xl:text-sm font-medium text-center">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {vaccination.map((field, index) => (
                                    <tr
                                      key={field?.id}
                                      className={`${
                                        vaccinationData?.parsed_data?.[index]?.discrepancy ? "bg-[#fff7ec]" : ""
                                      }`}
                                    >
                                      <td className="p-3 text-[10px] sm:text-xs 2xl:text-sm pl-3">{index + 1}</td>
                                      <td className="p-3 text-[10px] sm:text-xs 2xl:text-sm w-36">
                                        <div className="w-36">
                                          {/* <PWLCalendarComponent
                                          showLeftNav={false}
                                          allowPastDaySelection={true}
                                          errorMessages={[{ type: "required", message: "Please select valid DOB" }]}
                                          register={formHook.register("dob", {
                                            required: true,
                                          })}
                                          showTypedErrors
                                          defaultSelectedDate={formHook.watch("dob")}
                                          
                                          disabledFutureDateYearMonthSelection
                                          fieldError={formHook.formState.errors.dob}
                                          onSelectDate={(date) => {
                                            const dobString = convertToValidDateStringFormat(date);
                                            formHook.setValue("dob", dobString);
                                            const d = convertDOBToYMDFormat(dobString);
                                            formHook.setValue("day", d.days);
                                            formHook.setValue("month", d.months);
                                            formHook.setValue("year", d.years);
                                          }}
                                          className="w-full"
                                        /> */}

                                          <PWLCalendarComponent
                                            allowPastDaySelection={true}
                                            disableDatesAfterDate={getCurrentDate()}
                                            errorMessages={[{ message: "", type: "required" }]}
                                            register={formHook.register(
                                              `vaccination_ocr.${index}.given_date` as const,
                                              {
                                                required: true,
                                              },
                                            )}
                                            disabledFutureDateYearMonthSelection
                                            fieldError={formHook.formState.errors[`date-${index}` as const]}
                                            onSelectDate={(date) => {
                                              formHook.setValue(
                                                `vaccination_ocr.${index}.given_date`,
                                                format(date, "yyyy-MM-dd"),
                                              );
                                            }}
                                            defaultSelectedDate={formHook.watch(`vaccination_ocr.${index}.given_date`)}
                                            className="w-full"
                                            showLeftNav={false}
                                          />
                                        </div>
                                      </td>
                                      {mode === "UPLOAD" && (
                                        <td className="p-3 text-[10px] sm:text-xs 2xl:text-sm">
                                          <p>{formHook.getValues(`vaccination_ocr.${index}.original_vaccine_name`)}</p>
                                        </td>
                                      )}
                                      <td className="p-3 text-[10px] sm:text-xs 2xl:text-sm">
                                        <div className="">
                                          <SearchableSelectMenu
                                            errorMessages={[{ message: "", type: "required" }]}
                                            onSelectItem={(item) => {
                                              if (item) {
                                                formHook.setValue(`vaccination_ocr.${index}.vaccine_name`, item.title);
                                                formHook.setValue(`vaccination_ocr.${index}.unique_dose_key`, item.id);
                                              }
                                            }}
                                            fieldError={
                                              formHook?.formState?.errors?.vaccination_ocr
                                                ? formHook?.formState?.errors?.vaccination_ocr[index]
                                                  ? formHook?.formState?.errors?.vaccination_ocr[index]?.unique_dose_key
                                                  : null
                                                : null
                                            }
                                            register={formHook.register(`vaccination_ocr.${index}.unique_dose_key`, {
                                              required: true,
                                            })}
                                            selectItems={staticDataIteams}
                                            placeholder="Select Vaccination"
                                            showTooltips={true}
                                            showTypedErrors={false}
                                            showDropdownIcon={true}
                                            defaultSelected={
                                              staticDataIteams?.filter(
                                                (oc) =>
                                                  oc.id === formHook.watch(`vaccination_ocr.${index}.unique_dose_key`),
                                              )[0]
                                            }
                                            listBoxClassName="w-full"
                                            className="text-gray-800 "
                                            containerClassName="w-full"
                                          />
                                        </div>
                                      </td>
                                      <td className="p-3 text-[10px] sm:text-xs 2xl:text-sm text-center">
                                        <button
                                          type="button"
                                          className="text-red-500 text-xs font-semibold text-center"
                                        >
                                          <XMarkIcon
                                            className="w-5 h-5 text-red-500 font-bold"
                                            onClick={() => removeVaccine(index)}
                                          />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {mode === "UPLOAD" && (
                              <button
                                type="button"
                                className="text-white mt-3 bg-black text-xs font-semibold flex items-center gap-2 py-1.5 px-3 rounded-lg border-transparent"
                                onClick={() =>
                                  addVaccination({
                                    vaccine_name: "",
                                    dose_number: null,
                                    given_date: new Date().toISOString().split("T")[0],
                                    unique_dose_key: null,
                                  })
                                }
                              >
                                Add Vaccine <PlusIcon className="w-4 h-4" />
                              </button>
                            )}
                            <p className="text-xs text-[#475467] pb-3 py-4">
                              Please check all the rows highlighted with yellow, and double check if "Given Date" and
                              "Vaccination Name" are correct. You can edit these fields if any changes are required.
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          {vaccinationErrorMessage ? (
                            <p className="text-red-500 text-center text-sm">{vaccinationErrorMessage}</p>
                          ) : (
                            <p className="text-xs text-center p-10">No Record's detected </p>
                          )}
                        </>
                      )}
                    </div>
                    {vaccination?.length > 0 && (
                      <div className="flex items-start gap-2 text-[10px] sm:text-xs 2xl:text-xs pr-10 ">
                        <SimpleCheckbox
                          onChange={(e) => {
                            e.stopPropagation(); // ✅ Correct spelling and function call
                            setToggle((prev) => !prev);
                          }}
                          checked={toggle}
                          id={"ABC"}
                          colorClassName="text-black"
                          descriptionContent={
                            <label htmlFor="ABC" className="ml-3 block text-[10px] sm:text-xs 2xl:text-sm leading-6  ">
                              I’ve cross-checked the uploaded document. All the data is accurate to the best of my
                              knowledge.
                            </label>
                          }
                        />
                      </div>
                    )}
                    <div className="flex justify-end gap-4 mt-14 pb-6">
                      <button
                        type="submit"
                        onClick={() => {
                          updatedVaccination([]); // Clear the field array
                          setVaccinationData([]);
                          formHook.reset({ vaccination_ocr: [] }); // Ensure reset has matching structure
                          setInitialState(false); // Reset initial state
                          setFileData(null); // Optional: Clear fileData too
                          setOpen(false); // Close modal
                        }}
                        className="rounded-md bg-[#9fa7b21a] px-5 py-2 text-[10px] sm:text-xs 2xl:text-sm text-[#000000] font-medium "
                      >
                        Cancel
                      </button>
                      <button
                        disabled={!toggle || vaccination?.length === 0}
                        type="submit"
                        className={`rounded-md ${toggle && vaccination?.length > 0 ? "bg-[#000000]" : "bg-[#949494] cursor-not-allowed"}  font-medium px-6 py-2 text-[10px] sm:text-xs 2xl:text-sm text-white `}
                      >
                        {isSubmitPending ? (
                          <div className="relative text-center rtl:text-right w-14 items-center">
                            <div role="status">
                              <svg
                                aria-hidden="true"
                                className="inline w-5 h-5 text-gray-200 animate-spin  fill-blue-600"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                  fill="currentFill"
                                />
                              </svg>
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default OCRmodalComponent;

const TableSkeleton = () => {
  return (
    <div className="bg-white mt-8 h-[300px] overflow-scroll animate-pulse">
      <table className="rounded-md divide-y divide-gray-200 min-w-full">
        <thead className="top-0 sticky bg-[#F7F7F7] shadow-sm rounded-md">
          <tr>
            {/* Skeleton Header */}
            {[...Array(3)].map((_, index) => (
              <th
                key={index}
                className="px-3 py-3 text-left text-xs font-medium text-gray-300 capitalize tracking-wider"
              >
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {/* Skeleton Rows */}
          {[...Array(3)].map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b">
              {[...Array(6)].map((_, colIndex) => (
                <td key={colIndex} className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-300">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-300">
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
